<script setup>
import { computed, useSlots } from 'vue';
import { useRoute } from 'vue-router';
import { settingsBarVisible } from '../settingsBack';
import CustomBrandPolicyWrapper from 'dashboard/components/CustomBrandPolicyWrapper.vue';
import { getHelpUrlForFeature } from '../../../../helper/featureHelper';
import BackButton from '../../../../components/widgets/BackButton.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  linkText: {
    type: String,
    default: '',
  },
  featureName: {
    type: String,
    default: '',
  },
  backButtonLabel: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
});

const slots = useSlots();
const route = useRoute();
// Com a barra de contexto na tela, o voltar é dela; não repetir aqui.
const hasContextBack = computed(() => settingsBarVisible(route));

const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const helpURL = getHelpUrlForFeature(props.featureName);
</script>

<template>
  <div class="flex flex-col items-start w-full">
    <BackButton
      v-if="backButtonLabel && !hasContextBack"
      compact
      :button-label="backButtonLabel"
      class="my-1"
    />
    <div
      v-if="title"
      class="flex items-center justify-between w-full gap-4 min-h-8 mb-2"
    >
      <h1 class="text-heading-1 text-n-slate-12">
        {{ title }}
      </h1>
    </div>
    <div
      v-if="description || $slots.description || linkText || helpURL"
      class="flex flex-col w-full gap-1.5 text-n-slate-11"
    >
      <p
        v-if="description || $slots.description"
        class="mb-0 line-clamp-5 sm:line-clamp-none max-w-3xl text-body-main"
      >
        <slot name="description">{{ description }}</slot>
      </p>
      <CustomBrandPolicyWrapper :show-on-custom-branded-instance="false">
        <a
          v-if="helpURL && linkText"
          :href="helpURL"
          target="_blank"
          rel="noopener noreferrer"
          class="items-center hidden gap-1 text-sm font-medium sm:inline-flex w-fit text-n-blue-11 hover:underline mb-2"
        >
          {{ linkText }}
          <Icon
            icon="i-ph-caret-right"
            class="flex-shrink-0 text-n-blue-11 size-4"
          />
        </a>
      </CustomBrandPolicyWrapper>
    </div>
  </div>
  <div
    v-if="searchPlaceholder || slots.actions || slots.tabs"
    class="mt-4 flex min-w-0 flex-wrap items-center justify-between gap-3"
  >
    <div
      v-if="slots.tabs || searchPlaceholder"
      class="flex min-w-0 w-full flex-wrap items-center gap-3 sm:w-auto"
    >
      <slot name="tabs" />
      <Input
        v-if="searchPlaceholder"
        v-model="searchQuery"
        :placeholder="searchPlaceholder"
        :aria-label="searchPlaceholder"
        class="group flex w-full min-w-0 sm:w-56 [&>input]:!m-0 [&>input]:ltr:!pl-10 [&>input]:rtl:!pr-10 [&>input]:!rounded-[var(--workspace-radius,0.625rem)]"
        size="sm"
        type="search"
      >
        <template #prefix>
          <span
            class="pointer-events-none absolute inset-y-0 start-3 z-10 flex items-center"
          >
            <Icon
              icon="i-ph-magnifying-glass"
              aria-hidden="true"
              class="size-4 text-n-slate-11 group-focus-within:text-n-brand"
            />
          </span>
        </template>
      </Input>
    </div>
    <div
      class="flex min-w-0 flex-wrap items-center gap-3"
      :class="{ 'flex-row-reverse sm:flex-row': !slots.tabs }"
    >
      <slot name="count" />
      <div
        v-if="slots.count"
        class="w-px h-3 rounded-xl bg-n-weak ltr:ml-1 ltr:mr-2 rtl:ml-2 rtl:mr-1 flex-shrink-0"
      />
      <slot name="actions" />
    </div>
  </div>
</template>
